import { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeUpdateContext } from '../../App';
import { setSurveyTheme } from '../../redux/survey/actions';
import { learningDefaults } from '../constants/learningDefaults';
import { fetchLearningBrandDetails } from '../helpers/fetchLearningBrandDetails';
import { setLearningBrandSuccess } from '../../redux/learning/actions';
import {
  selectLearningBrandPackageId,
  selectLearningPackageData,
} from '../../redux/learning/selectors';

/** Loads company brand from package `company_id` / `survey_id` after package fetch. */
export function useLearningBrand() {
  const dispatch = useDispatch();
  const { changeTheme } = useContext(ThemeUpdateContext);
  const packageData = useSelector(selectLearningPackageData);
  const loadedBrandPackageId = useSelector(selectLearningBrandPackageId);

  useEffect(() => {
    if (!packageData?._id) {
      return undefined;
    }

    if (loadedBrandPackageId === packageData._id) {
      return undefined;
    }

    const companyId = packageData.company_id;
    const surveyId = packageData.survey_id?._id;

    if (!companyId && !surveyId) {
      return undefined;
    }

    let cancelled = false;

    const loadBrand = async () => {
      try {
        const brand = await fetchLearningBrandDetails({ companyId, surveyId });
        if (cancelled || !brand) {
          return;
        }

        if (companyId) {
          localStorage.setItem('company_id', companyId);
        }

        const logoSrc = brand.logo || learningDefaults.logoSrc;
        if (brand.logo) {
          localStorage.setItem('logo', brand.logo);
        }

        const theme = {
          primaryColor: brand.primaryColor,
          secondaryColor: brand.secondaryColor,
          backgroundColor: brand.backgroundColor,
          highlightColor: brand.highlightColor,
          fontColor: brand.fontColor,
          logo: brand.logo,
        };

        dispatch(setSurveyTheme(theme));
        changeTheme?.(theme);
        dispatch(setLearningBrandSuccess({
          logoSrc,
          packageId: packageData._id,
        }));
      } catch {
        if (!cancelled) {
          dispatch(setLearningBrandSuccess({
            logoSrc: learningDefaults.logoSrc,
            packageId: packageData._id,
          }));
        }
      }
    };

    loadBrand();

    return () => {
      cancelled = true;
    };
  }, [
    changeTheme,
    dispatch,
    loadedBrandPackageId,
    packageData?._id,
    packageData?.company_id,
    packageData?.survey_id?._id,
  ]);
}
